<template>
  <div>
    <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="6000">
      <span v-if="snackbar_msg != null">{{ snackbar_msg }}</span>
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-card class="main-card" flat>
      <v-tabs v-model="tabs" background-color="transparent" centered grow show-arrows slider-color="transparent"
        class="custom-tabs">
        <v-tab v-if="profile_relative_tab.indexOf('Under_medical_tab_medical_details') !== -1"
          :class="['custom-tab', 'first-tab', { 'active-tab': tabs === 0 }]">
          <v-icon class="mr-2">mdi-medical-bag</v-icon>Medical Details
        </v-tab>

        <v-tab v-if="profile_relative_tab.indexOf('Under_medical_tab_family_physician') !== -1"
          :class="['custom-tab', { 'active-tab': tabs === 1 }]">
          <v-icon class="mr-2">mdi mdi-doctor</v-icon>Family Physician
        </v-tab>

        <v-tab v-if="profile_relative_tab.indexOf('Under_medical_tab_health_problems') !== -1"
          :class="['custom-tab', { 'active-tab': tabs === 2 }]">
          <v-icon class="mr-2">mdi mdi-stethoscope</v-icon>Health Problems
        </v-tab>

        <v-tab v-if="profile_relative_tab.indexOf('Under_medical_tab_family_vaccination_details') !== -1"
          :class="['custom-tab', { 'active-tab': tabs === 3 }]">
          <v-icon class="mr-2">mdi mdi-needle</v-icon>Vaccination Details
        </v-tab>

        <v-tab v-if="profile_relative_tab.indexOf('Under_medical_tab__health_reports') !== -1"
          :class="['custom-tab', 'last-tab', { 'active-tab': tabs === 4 }]">
          <v-icon class="mr-2">mdi mdi-file-document-check</v-icon>Health Reports
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tabs">
        <v-tab-item v-if="profile_relative_tab.indexOf('Under_medical_tab_medical_details') !== -1">
          <v-card flat>
            <v-row dense class="pa-5">
              <!-- Dynamic Fields -->
              <v-col v-for="(field, key) in medical_details" :key="key" v-if="field && field.isactive" cols="12" sm="4"
                md="4" class="pt-0">
                <!-- Label -->
                <span class="pl-1 pb-2" style="font-size: 14px;">
                  <b>{{ field.displayName }}</b>
                </span>
                <v-icon v-if="field.required" class="pl-1 pb-2" color="error">*</v-icon>

                <!-- Checkbox -->
                <v-checkbox v-if="field.type === 'checkbox'" v-model="field.value" :readonly="!field.iseditable"
                  :filled="!field.iseditable" density="compact" class="font-weight-bold" />

                <!-- Text input -->
                <v-text-field v-else-if="field.type === 'text'" v-model.trim="field.value" outlined dense rounded
                  :readonly="!field.iseditable" :filled="!field.iseditable"
                  :type="isHeightOrWeight(field.displayName) ? 'number' : 'text'"
                  :rules="isHeightOrWeight(field.displayName) ? [v => /^\d*\.?\d*$/.test(v) || 'Only numbers are allowed'] : []"
                  @keypress="isHeightOrWeight(field.displayName) ? onlyDigits($event) : null" />
              </v-col>
            </v-row>

            <v-row dense justify="center">
              <v-btn @click="saveMedicalDetails()" :loading="save_loader" text class="button-77">Save</v-btn>
            </v-row>
            <br />
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="profile_relative_tab.indexOf('Under_medical_tab_family_physician') !== -1">
          <v-card flat>
            <v-row dense class="pa-5">
              <!-- Dynamic Fields -->
              <v-col v-for="(field, key) in family_physician_details" :key="key" v-if="field && field.isactive"
                cols="12" sm="4" md="4" class="pt-0">
                <!-- Label -->
                <span class="pl-1 pb-2" style="font-size: 14px;">
                  <b>{{ field.displayName }}</b>
                </span>
                <v-icon v-if="field.required" class="pl-1 pb-2" color="error">*</v-icon>

                <!-- Checkbox -->
                <v-checkbox v-if="field.type === 'checkbox'" v-model="field.value" :readonly="!field.iseditable"
                  :filled="!field.iseditable" density="compact" class="font-weight-bold" />

                <!-- Text input -->
                <v-text-field v-else-if="field.type === 'number' && isStrictContactField(field.displayName)"
                  v-model.trim="field.value" type="text" inputmode="numeric" maxlength="10"
                  @input="enforce10Digits(field)" @keydown="prevent11thDigit($event, field)"
                  @paste.prevent="pasteOnly10Digits($event, field)" :counter="10" :rules="strictContactRules"
                  hint="Exactly 10 digits required" persistent-hint outlined dense rounded :readonly="!field.iseditable"
                  :filled="!field.iseditable" />

                <v-text-field v-else-if="field.type === 'text'" v-model.trim="field.value" outlined dense rounded
                  :readonly="!field.iseditable" :filled="!field.iseditable" />

                <!-- Textarea -->
                <v-textarea v-else-if="field.type === 'textarea'" v-model.trim="field.value"
                  :readonly="!field.iseditable" :filled="!field.iseditable" dense outlined rounded />
              </v-col>
            </v-row>
            <v-row dense justify="center">
              <v-btn @click="saveFamilyPhysician()" :loading="save_loader" text class="button-77">Save</v-btn>
            </v-row>
            <br />
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="profile_relative_tab.indexOf('Under_medical_tab_health_problems') !== -1">
          <v-card flat>
            <v-data-table :headers="dynamicHealthProblemHeaders" :items="health_problems_list"
              sort-by="health_problem_date" class="elevation-1" dense style="padding: 10px" :search="search"
              hide-default-header>
              <template v-slot:header="{ props }">
                <thead>
                  <tr>
                    <th v-for="header in props.headers" :key="header.value" class="custom-header">
                      {{ header.text }}
                    </th>
                  </tr>
                </thead>
              </template>
              <template v-slot:top>
                <v-toolbar dense flat class="custom-toolbar ma-2">
                  <v-toolbar-title>Health Problems</v-toolbar-title>
                </v-toolbar>
                <v-row justify="left" dense>
                  <v-col sm="3" md="3" cols="12"></v-col>
                  <v-col sm="6" md="4" cols="12" class="pt-0 pb-5">
                    <v-text-field v-model="search" append-icon="search" label="Search" single-line
                      hide-details></v-text-field>
                  </v-col>
                  <v-dialog v-model="dialog" max-width="600px">
                    <template v-slot:activator="{ on }">
                      <v-btn class="mb-2 button" v-on="on" small>Add Health Problems</v-btn>
                    </template>
                    <v-card>
                      <v-form v-model="valid">
                        <v-card-title class="custom-toolbar">
                          <span class="headline">{{ formTitle }}</span>
                        </v-card-title>
                        <v-card-text>
                          <v-container>
                            <v-row class="px-5">
                              <v-col v-for="field in health_problem_fields" :key="field.field_name"
                                v-if="field.isactive" cols="12" sm="12" md="12" class="pt-0 pb-0">
                                <div class="pl-1 pb-2" style="font-size: 14px;">
                                  <b>{{ field.label }}</b>
                                  <span v-if="field.required" class="pl-1" style="color:red">*</span>
                                </div>
                                <v-text-field v-if="field.input_type === 'text'" v-model.trim="field.value"
                                  :readonly="!field.iseditable" :filled="!field.iseditable" dense outlined rounded />
                                <v-textarea v-else-if="field.input_type === 'textarea'" v-model.trim="field.value"
                                  :readonly="!field.iseditable" :filled="!field.iseditable" dense outlined rounded />
                                <v-text-field v-else-if="field.input_type === 'date'" v-model.trim="field.value"
                                  type="date" :readonly="!field.iseditable" :filled="!field.iseditable" dense outlined
                                  rounded />
                                <v-checkbox v-else-if="field.input_type === 'checkbox'" v-model="field.value"
                                  :label="field.label" :readonly="!field.iseditable" density="compact" class="mt-0" />
                                <v-select v-else-if="field.input_type === 'select' && !field.multiple"
                                  v-model="field.value" :items="field.options" item-text="name" item-value="id"
                                  :readonly="!field.iseditable" :filled="!field.iseditable" dense outlined rounded />
                                <v-autocomplete v-else-if="field.input_type === 'select' && field.multiple"
                                  v-model="field.value" :items="field.options" multiple :readonly="!field.iseditable"
                                  :filled="!field.iseditable" dense outlined rounded />
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                        <v-card-actions v-if="health_problem_fields.length > 0">
                          <v-spacer></v-spacer>
                          <v-btn class="mb-2 button" text @click="close" small>Cancel</v-btn>
                          <v-btn class="button-77" text @click="saveHealthProblem()" :loading="save_loader">Save</v-btn>
                        </v-card-actions>
                      </v-form>
                    </v-card>
                  </v-dialog>
                </v-row>
              </template>
              <template v-slot:item="{ item }">
                <tr>
                  <td v-for="field in item.fields" :key="field.field_name">
                    <span>{{ field.value || '-' }}</span>
                  </td>
                  <td>
                    <v-avatar size="25" class="edit-avatar mr-2">
                      <v-icon class="edit-v-icon mr-2" @click="editItem(item)" color="success">edit</v-icon>
                    </v-avatar>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <span>No health problems available.</span>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
        <v-tab-item v-if="profile_relative_tab.indexOf('Under_medical_tab_family_vaccination_details') !== -1">
          <v-card flat>
            <v-data-table :headers="headers_vaccination" :items="child_vaccination_list" :loading="fetch_loader"
              loading-text="Loading vaccination details..." sort-by="srno" class="elevation-1" dense
              style="padding: 10px" :search="search_vaccination" hide-default-header>
              <template v-slot:header="{ props }">
                <thead>
                  <tr>
                    <th v-for="header in props.headers" :key="header.value" class="custom-header">
                      {{ header.text }}
                    </th>
                  </tr>
                </thead>
              </template>
              <template v-slot:no-data>
                <span>No vaccination records available.</span>
              </template>
              <template v-slot:top>
                <v-toolbar dense flat class="custom-toolbar ma-2">
                  <v-toolbar-title>Vaccination Details</v-toolbar-title>
                </v-toolbar>
                <v-row justify="left" dense>
                  <v-col sm="3" md="3" cols="12"></v-col>
                  <v-col sm="6" md="4" cols="12" class="pt-0 pb-5">
                    <v-text-field v-model="search_vaccination" append-icon="search" label="Search" single-line
                      hide-details></v-text-field>
                  </v-col>
                  <v-dialog v-model="dialog_vaccin" max-width="600px">
                    <template v-slot:activator="{ on }">
                      <v-btn class="mb-2 button" v-on="on" small>Add Vaccination Details</v-btn>
                    </template>
                    <v-card>
                      <v-form v-model="valid">
                        <v-card-title class="custom-toolbar">
                          <span class="headline">{{ VaccinationFormTitle }}</span>
                        </v-card-title>
                        <v-card-text>
                          <v-container>
                            <v-row>
                              <v-col v-for="field in vaccination_fields" :key="field.field_name" cols="12" sm="12"
                                md="12" class="pt-0 pb-0">
                                <span class="pl-1 pb-2" style="font-size: 14px;">
                                  <b>{{ field.label }}</b>
                                </span>
                                <v-icon v-if="field.required" class="pl-1 pb-2" color="error">*</v-icon>
                                <v-text-field v-if="field.input_type === 'text'"
                                  v-model.trim="editedItem_vaccination[field.field_name]" persistent-hint outlined
                                  rounded dense :readonly="!field.iseditable" :filled="!field.iseditable" />
                                <v-text-field v-else-if="field.input_type === 'date'"
                                  v-model.trim="editedItem_vaccination[field.field_name]" type="date" persistent-hint
                                  outlined rounded dense :readonly="!field.iseditable" :filled="!field.iseditable" />
                                <v-checkbox v-else-if="field.input_type === 'checkbox'"
                                  v-model="editedItem_vaccination[field.field_name]" :label="field.label"
                                  :readonly="!field.iseditable" :filled="!field.iseditable" density="compact"
                                  class="mt-0" />
                                <v-select v-else-if="field.input_type === 'select'"
                                  v-model="editedItem_vaccination[field.field_name]" :items="field.options"
                                  item-text="name" item-value="id" persistent-hint outlined rounded dense
                                  :readonly="!field.iseditable" :filled="!field.iseditable"
                                  placeholder="Select a vaccine" />
                                <v-file-input v-else-if="field.input_type === 'file'"
                                  v-model="editedItem_vaccination[field.field_name]" accept="image/*,.pdf" outlined
                                  rounded dense persistent-hint :readonly="!field.iseditable"
                                  :filled="!field.iseditable">
                                  <template #label>
                                    <span>{{ field.label }} <span v-if="field.required"
                                        style="color: red">*</span></span>
                                  </template>
                                </v-file-input>
                                <span v-else>
                                  Unknown field type: {{ field.input_type }} for {{ field.field_name }}
                                </span>
                                <span
                                  v-if="editedItem_vaccination.attachment && field.field_name === 'Under_medical_tab__vaccination_attachment'">
                                  <a :href="awspath + editedItem_vaccination.attachment" target="_blank">View
                                    Attachment</a>
                                </span>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                        <v-card-actions v-if="vaccination_fields.length > 0">
                          <v-spacer></v-spacer>
                          <v-btn class="mb-2 button" text @click="dialog_vaccin_close">Cancel</v-btn>
                          <v-btn class="button-77" text @click="saveVaccineRecord"
                            :loading="save_loader_VaccineRecord">Save</v-btn>
                        </v-card-actions>
                      </v-form>
                    </v-card>
                  </v-dialog>
                </v-row>
              </template>
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.srno }}</td>
                  <td v-for="field in item.fields" :key="field.field_name">
                    <span
                      v-if="field.field_name === 'Under_medical_tab__vaccination_attachment' && field.value !== 'NA'">
                      <v-icon color="primary" @click="openDoc(field.value)" class="cursor-pointer">mdi-eye</v-icon>
                    </span>
                    <span v-else-if="field.field_name === 'Under_medical_tab__vaccination_iscompliance'">
                      <v-icon v-if="field.value" style="color: green"
                        class="cursor-pointer">mdi-checkbox-marked-circle</v-icon>
                      <v-icon v-else style="color: red" class="cursor-pointer">mdi-close-circle</v-icon>
                    </span>
                    <span v-else>{{ field.value || '-' }}</span>
                  </td>
                  <td>
                    <v-avatar size="35">
                      <v-icon class="mr-4 v-icon-size" color="red"
                        @click="deleteDatavaccination(item)">mdi-trash-can</v-icon>
                    </v-avatar>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
        <v-tab-item v-if="profile_relative_tab.indexOf('Under_medical_tab__health_reports') !== -1">
          <v-card flat>
            <v-data-table :headers="headers_health_report" :items="health_cards" sort-by="srno" class="elevation-1"
              dense style="padding: 10px" :search="search_health_report" hide-default-header>
              <template v-slot:header="{ props }">
                <thead>
                  <tr>
                    <th v-for="header in props.headers" :key="header.value" class="custom-header">
                      {{ header.text }}
                    </th>
                  </tr>
                </thead>
              </template>
              <template v-slot:top>
                <v-toolbar dense flat class="custom-toolbar ma-2">
                  <v-toolbar-title>Health Reports</v-toolbar-title>
                </v-toolbar>
                <v-row justify="left" dense>
                  <v-col sm="3" md="3" cols="12"></v-col>
                  <v-col sm="6" md="4" cols="12" class="pt-0 pb-5">
                    <v-text-field v-model="search_health_report" append-icon="search" label="Search" single-line
                      hide-details></v-text-field>
                  </v-col>
                  <v-dialog v-model="dialog_report" max-width="600px">
                    <template v-slot:activator="{ on }">
                      <v-btn class="button" v-on="on" small @click="prepareNewHealthReport">Add Health Report</v-btn>
                    </template>
                    <v-card>
                      <v-form v-model="valid">
                        <v-card-title class="custom-toolbar">
                          <span class="headline">{{ formTitlehealthReport }}</span>
                        </v-card-title>
                        <v-card-text>
                          <v-container>
                            <v-row>
                              <v-col v-for="field in fieldConfigs" :key="field.field_name" cols="12" sm="12" md="12"
                                class="pt-0 pb-0">
                                <span class="pl-1 pb-2" style="font-size: 14px;">
                                  <b>{{ field.label }}</b>
                                </span>
                                <v-icon v-if="field.required" class="pl-1 pb-2" color="error">*</v-icon>
                                <v-text-field
                                  v-if="field.field_name === 'Under_medical_tab__health_reports_upload_date'"
                                  v-model="editedItem_health[field.field_name]" type="date" persistent-hint outlined
                                  rounded dense :readonly="!field.iseditable" :filled="!field.iseditable" />
                                <v-text-field
                                  v-else-if="field.field_name === 'Under_medical_tab__health_reports_documentname'"
                                  v-model="editedItem_health[field.field_name]" persistent-hint outlined rounded dense
                                  :readonly="!field.iseditable" :filled="!field.iseditable" />
                                <v-textarea
                                  v-else-if="field.field_name === 'Under_medical_tab__health_reports_description'"
                                  v-model="editedItem_health[field.field_name]" persistent-hint outlined rounded dense
                                  :readonly="!field.iseditable" :filled="!field.iseditable" />
                                <v-file-input v-else-if="field.field_name === 'Under_medical_tab__health_reports_files'"
                                  v-model="editedItem_health[field.field_name]" accept="image/*,.pdf" outlined rounded
                                  dense persistent-hint :readonly="!field.iseditable" :filled="!field.iseditable">
                                  <template #label>
                                    <span>Upload Health Report Document
                                      <span v-if="field.required" style="color: red">*</span></span>
                                  </template>
                                </v-file-input>
                                <span
                                  v-if="editedItem_health.attachment && field.field_name === 'Under_medical_tab__health_reports_files'">
                                  <a :href="awspath + editedItem_health.attachment" target="_blank">View Attachment</a>
                                </span>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn class="mb-2 button" text small @click="closehealthreport">Cancel</v-btn>
                          <v-btn class="button-77" text @click="saveHealthReport()"
                            :loading="save_loader_report">Save</v-btn>
                        </v-card-actions>
                      </v-form>
                    </v-card>
                  </v-dialog>
                </v-row>
              </template>
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.srno }}</td>
                  <td v-for="field in item.fields" :key="field.field_name">
                    <span v-if="field.field_name === 'Under_medical_tab__health_reports_files' && field.value !== 'NA'">
                      <v-icon color="primary" @click="openDoc(field.value)" class="cursor-pointer">mdi-eye</v-icon>
                    </span>
                    <span v-else>{{ field.value || '-' }}</span>
                  </td>
                  <!-- <td>
                    <v-avatar size="25" class="edit-avatar mr-2">
                      <v-icon class="edit-v-icon mr-2" @click="editItemReport(item)" color="success">edit</v-icon>
                    </v-avatar>
                  </td>
                  <td>
                    <v-avatar size="35">
                      <v-icon class="mr-4 v-icon-size" color="red" @click="deleteData(item)">mdi-trash-can</v-icon>
                    </v-avatar>
                  </td> -->
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
  import UnderDevelopement from "@/components/shared/UnderDevelopement.vue";
  import axios from "axios";
  import moment from "moment";

  export default {
    props: {
      learner: {
        type: [String, Number],
        required: true,
      },
      profile_relative_tab: {
        type: Array,
        default: () => []
      }
    },
    components: { UnderDevelopement },
    data() {
      return {
        save_loader: false,
        fetch_loader: false,
        save_loader_report: false,
        // mobileRules: [
        //   v => !!v || 'Contact number is required',
        //   v => /^\d{10}$/.test(v) || 'Enter a valid 10-digit mobile number',
        //   v => !/^0+/.test(v) || 'Contact number cannot start with zero'
        // ],
        mobileRules: [
          v => !!v || 'Contact number is required',
          v => /^\d{10}$/.test(v) || 'Enter a valid 10-digit mobile number',
          v => !/^0+/.test(v) || 'Contact number cannot start with zero',
          v => /^[0-9]+$/.test(v) || 'Only digits are allowed',
        ],
        editedIndex: -1,
        editedIndex_health: -1,
        editedIndex_vaccination: -1,
        editedItem: {
          id: "",
          health_problem_date: "",
          health_problem_nature: "",
          health_problem_remark: "",
          health_problem_medication_administered:"",
          health_problem_name_of_medical_professional:"",
        },
        defaultItem: {
          health_problem_date: "",
          id: "",
          health_problem_nature: "",
          health_problem_remark: "",
          health_problem_medication_administered: "",
          health_problem_name_of_medical_professional: "",
        },
        editedItem_vaccination: {
          id: "",
          'Under_medical_tab__vaccination_date': "",
          'Under_medical_tab__vaccination_name': "",
          'Under_medical_tab__vaccination_iscompliance': false,
          'Under_medical_tab__vaccination_attachment': null,
          attachment: null,
        },
        defaultItem_vaccination: {
          id: "",
          'Under_medical_tab__vaccination_date': "",
          'Under_medical_tab__vaccination_name': "",
          'Under_medical_tab__vaccination_iscompliance': false,
          'Under_medical_tab__vaccination_attachment': null,
          attachment: null,
        },
        valid: true,
        vaccine_id: "",
        menu: false,
        healthmenu: false,
        vacccineCheckbox: false,
        save_loader_VaccineRecord: false,
        vaccine: "",
        date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        proof_doc: null,
        tabs: 0,
        dialog: false,
        dialog_vaccin: false,
        dialog_report: false,
        awspath: null,
        health_problems_list: [],
        vaccination_master_list: [],
        child_vaccination_list: [],
        vaccination_fields: [],
        health_cards: [],
        color: "",
        search_health_report: "",
        snackbar: false,
        snackbar_msg: "",
        editable: "",
        child_div_id: "",
        search: "",
        search_vaccination: "",
        medical_details: {},
        family_physician_details: {},
        strictContactRules: [
          v => !!v || 'Required',
          v => (v && v.length === 10) || 'Exactly 10 digits required',
          v => /^\d+$/.test(v) || 'Only digits allowed',
          v => !/^0/.test(v) || 'Cannot start with 0'
        ],
        editedItem_health: {
          id: "",
          'Under_medical_tab__health_reports_upload_date': "",
          'Under_medical_tab__health_reports_documentname': "",
          'Under_medical_tab__health_reports_description': "",
          'Under_medical_tab__health_reports_files': null,
          attachment: null,
        },
        defaultItem_health: {
          id: "",
          'Under_medical_tab__health_reports_upload_date': "",
          'Under_medical_tab__health_reports_documentname': "",
          'Under_medical_tab__health_reports_description': "",
          'Under_medical_tab__health_reports_files': null,
          attachment: null,
        },
        headers: [
          { text: "Date", align: "left", sortable: false, value: "health_problem_date" },
          { text: "Nature Of Health Problem", align: "left", sortable: false, value: "health_problem_nature" },
          { text: "Remark", align: "left", sortable: true, value: "health_problem_remark" },
          { text: "Medication Administered", align: "left", sortable: true, value: "health_problem_medication_administered" },
          { text: "Name of Medical Professional", align: "left", sortable: true, value: "health_problem_name_of_medical_professional" },
          { text: "Edit", value: "action", sortable: false },
        ],
        headers_vaccination: [
          { text: "Sr.No", align: "left", sortable: false, value: "srno" },
          { text: "Vaccination Date", align: "left", sortable: false, value: "Under_medical_tab__vaccination_date" },
          { text: "Vaccine Name", align: "left", sortable: false, value: "Under_medical_tab__vaccination_name" },
          { text: "Is Completed ?", align: "left", sortable: false, value: "Under_medical_tab__vaccination_iscompliance" },
          { text: "Attachment", align: "left", sortable: true, value: "Under_medical_tab__vaccination_attachment" },
          { text: "Delete", value: "delete", sortable: false },
        ],
        headers_health_report: [
          {
            text: "Sr.No",
            align: "left",
            sortable: false,
            value: "srno",
          },
          {
            text: "Health Report Date",
            align: "left",
            sortable: false,
            value: "upload_date",
          },
          {
            text: "Health Report Name",
            align: "left",
            sortable: false,
            value: "documentname",
          },
          {
            text: "Description",
            align: "left",
            sortable: false,
            value: "description",
          },
          {
            text: "Attachment",
            align: "left",
            sortable: true,
            value: "attachment",
          },
          // { text: "Edit", value: "action", sortable: false },
          // { text: "Delete", value: "delete", sortable: false },
        ],
        health_problem_fields: [],
      };
    },
    watch: {
      learner(newVal, oldVal) {
        if (newVal) {
          this.getMedicalDetails();
        }
      },
    },
    computed: {
      formTitle() {
        return this.editedIndex === -1
          ? "Add Health Problem"
          : "Edit Health Problem";
      },
      VaccinationFormTitle() {
        return this.editedIndex === -1
          ? "Add Vaccination Details"
          : "Edit Vaccination Details";
      },
      formTitlehealthReport() {
        return this.editedIndex_health === -1
          ? "Add Health Report Details"
          : "Edit Health Report Details";
      },
      dynamicHealthProblemHeaders() {
        if (!this.health_problem_fields || this.health_problem_fields.length === 0) {
          return [
            { text: 'No fields configured', value: 'nofield', sortable: false }
          ];
        }

        const headers = this.health_problem_fields
          .filter(field => field.isactive)
          .map(field => ({
            text: field.label || field.field_name.replace(/_/g, ' ').toUpperCase(),
            value: field.field_name,
            sortable: true,
            align: 'left'
          }));

        headers.push({
          text: 'Actions',
          value: 'action',    // THIS IS CORRECT
          sortable: false,
          align: 'center',
          width: '100px'
        });

        return headers;
      }

    },
    methods: {
      // restrictToTenDigits(event, currentValue) {
      //   const char = String.fromCharCode(event.keyCode);
      //   if (!/[0-9]/.test(char)) {
      //     event.preventDefault();
      //     return;
      //   }
      //   if (currentValue && currentValue.length >= 10) {
      //     event.preventDefault();
      //   }
      // },

      isStrictContactField(displayName) {
        if (!displayName) return false;
        const lower = displayName.toLowerCase().trim();
        return lower.includes('family doctor contact') ||
          lower.includes('emrg hospital number');
      },

      enforce10Digits(field) {
        if (!this.isStrictContactField(field.displayName)) return;
        const clean = (field.value || '').replace(/\D/g, '').substring(0, 10);
        if (field.value !== clean) {
          this.$nextTick(() => field.value = clean);
        }
      },

      prevent11thDigit(e, field) {
        if (!this.isStrictContactField(field.displayName)) return;
        const current = (field.value || '').replace(/\D/g, '');
        if (current.length >= 10) {
          const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (!allowed.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
          }
        }
      },

      pasteOnly10Digits(e, field) {
        if (!this.isStrictContactField(field.displayName)) return;
        const pasted = (e.clipboardData || window.clipboardData).getData('text');
        const digits = pasted.replace(/\D/g, '').substring(0, 10);
        this.$nextTick(() => field.value = digits);
      },

      restrictToTenDigits(value) {
        if (value) {
          // Remove any non-digit characters
          const cleanedValue = value.replace(/\D/g, '');
          // Limit to 10 digits
          if (cleanedValue.length > 10) {
            return cleanedValue.slice(0, 10);
          }
          return cleanedValue;
        }
        return value;
      },
      isHeightOrWeight(displayName) {
        return displayName.toLowerCase().includes('height') || displayName.toLowerCase().includes('weight');
      },
      isContactNumber(displayName) {
        return displayName.toLowerCase().includes('emergency hospital number') ||
          displayName.toLowerCase().includes('family doctor contact');
      },
      getDynamicHeaders() {
        return this.fieldConfigs
          .filter(field => field.isactive)
          .map(field => ({
            text: field.label,
            align: "left",
            sortable: field.field_name !== 'Under_medical_tab__health_reports_files',
            value: field.field_name,
          }));
      },
      deleteData(item) {
        if (!item || !item.id) {
          this.showSnackbar("#b71c1c", "Invalid health report ID. Cannot delete.");
          console.error("Delete attempted with invalid item:", item);
          return;
        }
        const docName = item.fields.find(f => f.field_name === 'Under_medical_tab__health_reports_documentname').value || 'Unnamed';
        if (!confirm(`Are you sure you want to delete Health Report #${item.srno} (${docName})? This action cannot be undone.`)) {
          return;
        }
        const payload = {
          delete_id: item.id,
          ldid: this.child_div_id,
        };
        console.log("Sending delete request:", payload);
        this.save_loader_report = true;
        axios
          .post("/Parent/deleteChildHealthDetails", payload, {
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then((res) => {
            console.log("Delete response:", res.data);
            if (res.data.flag === "YES") {
              this.health_cards = res.data.health_cards || [];
              this.showSnackbar("#4caf50", res.data.snackbar || "Health report deleted successfully");
              this.dialog_report = false;
            } else {
              this.showSnackbar("#b71c1c", res.data.snackbar || "Failed to delete health report");
              if (res.data.error_trace) {
                sessionStorage.setItem("error-trace", res.data.error_trace);
              }
            }
          })
          .catch((error) => {
            console.error("Delete API error:", error);
            this.showSnackbar("#b71c1c", "Network error: Failed to delete health report");
          })
          .finally(() => {
            this.save_loader_report = false;
          });
      },
      deleteDatavaccination(item) {
        const data = {
          delete_id: item.id,
          ldid: this.child_div_id,
        };
        axios
          .post("/Parent/deleteDataVaccination", data)
          .then((res) => {
            if (res.data.flag == "YES") {
              this.showSnackbar("#4caf50", "Deleted Successfully...");
              this.child_vaccination_list = res.data.child_vaccination_list;
              this.dialog_report = false;
            } else {
              this.showSnackbar("#b71c1c", "Something went wrong!!!");
              sessionStorage.setItem("error-trace", res.data.error_trace);
              this.dialog_report = false;
            }
          })
          .catch((error) => {
            this.showSnackbar("#b71c1c", "Something went wrong...");
            this.dialog_report = false;
          })
          .finally(() => { });
      },
      saveVaccineRecord() {
        this.save_loader_VaccineRecord = true;
        const vaccine_id = this.editedItem_vaccination['Under_medical_tab__vaccination_name'];
        const date = this.editedItem_vaccination['Under_medical_tab__vaccination_date'];
        const iscompliance = this.editedItem_vaccination['Under_medical_tab__vaccination_iscompliance'];
        const proof_doc = this.editedItem_vaccination['Under_medical_tab__vaccination_attachment'];

        for (let field of this.vaccination_fields) {
          const value = this.editedItem_vaccination[field.field_name];

          // Check if field is required and empty/null/undefined
          if (field.required) {
            // For checkbox, value might be boolean false, so check accordingly
            if (field.input_type === 'checkbox') {
              if (value !== true) {
                this.showSnackbar("error", `Please check ${field.label}`);
                this.save_loader_VaccineRecord = false;
                return;
              }
            } else if (!value || (typeof value === 'string' && value.trim() === '')) {
              this.showSnackbar("error", `Please enter/select ${field.label}`);
              this.save_loader_VaccineRecord = false;
              return;
            }
          }

          // Custom validation example for date
          if (field.input_type === 'date' && value) {
            // You can add a date validation here (optional)
            // e.g. check if date is not in future, etc.
            // For now, just a dummy example:
            const dateVal = new Date(value);
            if (isNaN(dateVal.getTime())) {
              this.showSnackbar("error", `Invalid date for ${field.label}`);
              this.save_loader_VaccineRecord = false;
              return;
            }
          }

          // Add more validation logic here if needed (e.g., for file types, select values etc.)
        }

        // Special case: if iscompliance checked, proof_doc must be uploaded

        const attachmentField = this.vaccination_fields.find(
          f => f.field_name === "Under_medical_tab__vaccination_attachment"
        );

        // Apply correct validation
        if (attachmentField.required) {
          // If field is required and user enabled compliance
          if (iscompliance && !proof_doc) {
            this.showSnackbar("error", `Please upload ${attachmentField.label}`);
            this.save_loader_VaccineRecord = false;
            return;
          }
        }

        let formData = new FormData();
        formData.append("lid", this.child_div_id);
        formData.append("vaccine_id", vaccine_id);
        formData.append("date", date);
        formData.append("iscompliance", iscompliance);
        if (proof_doc) {
          formData.append("attachment", proof_doc);
        }

        axios
          .post("/Parent/saveChildVaccineDetails", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          })
          .then((res) => {
            console.log("Save Vaccine Response:", res.data);
            if (res.data.flag === "YES") {
              this.showSnackbar("success", res.data.snackbar || "Vaccination record saved successfully");
              this.child_vaccination_list = res.data.child_vaccination_list || [];
              this.dialog_vaccin = false;
            } else {
              this.showSnackbar("error", res.data.snackbar || "Failed to save vaccination record");
            }
          })
          .catch((error) => {
            console.error("Error saving vaccine record:", error);
            this.showSnackbar("error", "Network error: Failed to save vaccination record");
          })
          .finally(() => {
            this.save_loader_VaccineRecord = false;
            this.editedItem_vaccination = { ...this.defaultItem_vaccination };
          });
      },
      openDoc(proofDocument) {
        window.open(this.awspath + proofDocument, "_blank");
      },
      showSnackbar(clr, msg) {
        this.snackbar = true;
        this.color = clr;
        this.snackbar_msg = msg;
      },
      getMedicalDetails() {
        this.fetch_loader = true;
        const data = {
          learner: this.learner,
        };
        axios
          .post("/parent/getMedicalDetails", data)
          .then((res) => {
            console.log("API Response:", res.data);
            if (res.data.flag === "YES") {
              console.log("Health Problems List:", res.data.hprob_list);
              this.medical_details = res.data.medical_details;
              this.family_physician_details = res.data.family_physician_details;
              this.$set(this, 'health_problems_list', res.data.hprob_list || []);
              this.$set(this, 'child_vaccination_list', res.data.child_vaccination_list || []);
              this.$set(this, 'vaccination_fields', res.data.vaccination_fields || []);
              console.log("vaccination_fields:", this.vaccination_fields);
              this.health_cards = res.data.health_cards || [];
              this.fieldConfigs = [...res.data.fieldConfigs] || [];
              this.$set(this, 'health_problem_fields', [...res.data.health_problem_fields] || []);
              this.awspath = res.data.aws_path;
              this.child_div_id = res.data.child_div_id;
              if (!res.data.hprob_list) {
                console.warn("Warning: hprob_list is missing in the API response");
              }
              this.headers_health_report = [
                { text: "Sr.No", align: "left", sortable: false, value: "srno" },
                ...this.getDynamicHeaders(),
                // { text: "Edit", value: "action", sortable: false },
                // { text: "Delete", value: "delete", sortable: false },
              ];
            } else {
              this.showSnackbar("error", res.data.snackbar);
            }
          })
          .catch((error) => {
            console.error("Error fetching medical details:", error);
            this.showSnackbar("error", "Failed to fetch medical details");
          })
          .finally(() => {
            this.fetch_loader = false;
          });
      },

      saveMedicalDetails() {
        for (let key in this.medical_details) {
          const f = this.medical_details[key];

          if (f.required) {
            // ✅ For checkbox fields
            if (f.type === "checkbox" && (f.value === null || f.value === undefined || f.value === false)) {
              this.showSnackbar("error", `Please select ${f.displayName}`);
              return;
            }

            // ✅ For other fields
            if (f.type !== "checkbox" && (f.value === null || f.value === "")) {
              this.showSnackbar("error", `Please enter ${f.displayName}`);
              return;
            }

            // ✅ For numeric validation
            if (this.isHeightOrWeight(f.displayName) && !/^\d*\.?\d*$/.test(f.value)) {
              this.showSnackbar("error", `Please enter a valid number for ${f.displayName}`);
              return;
            }
          }
        }

        this.save_loader = true;
        let payload = { learner_id: this.learner };
        for (let key in this.medical_details) {
          payload[key] = this.medical_details[key].value;
        }

        axios
          .post("/parent/saveMedicalDetails", payload)
          .then((res) => {
            if (res.data.flag === "YES") {
              this.showSnackbar("success", res.data.snackbar);
            } else {
              this.showSnackbar("error", res.data.snackbar);
            }
          })
          .catch((err) => {
            console.error(err);
            this.showSnackbar("error", "Something went wrong while saving.");
          })
          .finally(() => {
            this.save_loader = false;
          });
      },

      saveFamilyPhysician() {
        for (let key in this.family_physician_details) {
          const f = this.family_physician_details[key];
          if (f.required) {
            if (f.type === "checkbox" && (f.value === null || f.value === undefined)) {
              this.showSnackbar("error", `Please select ${f.displayName}`);
              return;
            }
            if (f.type !== "checkbox" && (f.value === null || f.value === "")) {
              this.showSnackbar("error", `Please enter ${f.displayName}`);
              return;
            }
            if (this.isContactNumber(f.displayName)) {
              if (!/^\d{10}$/.test(f.value)) {
                this.showSnackbar("error", `Please enter a valid 10-digit number for ${f.displayName}`);
                return;
              }
              if (/^0+/.test(f.value)) {
                this.showSnackbar("error", `${f.displayName} cannot start with zero`);
                return;
              }
            }
          }
        }
        this.save_loader = true;
        let payload = { learner_id: this.learner };
        for (let key in this.family_physician_details) {
          payload[key] = this.family_physician_details[key].value;
        }
        axios
          .post("/parent/saveFamilyPhysician", payload)
          .then((res) => {
            if (res.data.flag === "YES") {
              this.showSnackbar("success", res.data.snackbar);
            } else {
              this.showSnackbar("error", res.data.snackbar);
            }
          })
          .catch((err) => {
            console.error("Error saving family physician details:", err);
            this.showSnackbar("error", "Failed to save details. Please try again.");
          })
          .finally(() => {
            this.save_loader = false;
          });
      },
      saveHealthProblem() {
        for (let field of this.health_problem_fields) {
          if (field.required) {
            if (
              field.value == null ||
              field.value === '' ||
              (Array.isArray(field.value) && field.value.length === 0)
            ) {
              const action = field.input_type === 'date' || field.input_type === 'select' ? 'select' : 'enter';
              this.showSnackbar("error", `Please ${action} ${field.label}`);
              return;
            }
          }
        }

        const fieldData = this.health_problem_fields.reduce((obj, field) => {
          obj[field.field_name] = Array.isArray(field.value) ? field.value.join(",") : field.value;
          return obj;
        }, {});

        const payload = {
          learner_id: this.learner,
          child_div_id: this.child_div_id,
          user: "Parent",
          ...fieldData,
        };

        if (this.editedIndex > -1) {
          payload.id = this.editedItem.id;
        }

        this.save_loader = true;

        axios
          .post(this.editedIndex > -1 ? "/parent/editChildHealthProblem" : "/healthCard/saveChildHealthProblem", payload)
          .then((res) => {
            if (res.data.flag === "YES") {
              this.showSnackbar("success", res.data.snackbar || "Health problem updated successfully.");

              if (res.data.hprob_list) {
                this.$set(this, 'health_problems_list', res.data.hprob_list);
              }


              this.resetHealthProblemForm();
              this.dialog = false;
            } else {
              this.showSnackbar("error", res.data.snackbar || "Failed to save health problem.");
              if (res.data.error_trace) {
                sessionStorage.setItem("error-trace", res.data.error_trace);
              }
            }
          })
          .catch((error) => {
            console.error("Error saving health problem:", error);
            this.showSnackbar("error", "Something went wrong!");
          })
          .finally(() => {
            this.save_loader = false;
          });
      },

      resetHealthProblemForm() {
        this.health_problem_fields.forEach(field => {
          if (Array.isArray(field.value)) {
            field.value = [];
          } else {
            field.value = '';
          }
        });
        this.editedIndex = -1;
        this.editedItem = null;
      },


      editItem(item) {
        this.editedIndex = this.health_problems_list.indexOf(item);

        this.health_problem_fields = this.health_problem_fields.map(field => {
          const match = item.fields.find(f => f.field_name === field.field_name);

          let value = match ? match.value : "";

          // जर input type date असेल, तर value ला YYYY-MM-DD format मध्ये convert करा
          if (field.input_type === 'date' && value) {
            value = this.formatDateForInput(value);
          }

          return { ...field, value };
        });

        this.editedItem = { ...item };
        this.dialog = true;
      },

      // Helper method to format date string to YYYY-MM-DD for input[type=date]
      formatDateForInput(dateStr) {
        const d = new Date(dateStr);
        if (isNaN(d)) return ''; // invalid date
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const year = d.getFullYear();
        return `${year}-${month}-${day}`;
      },
prepareNewHealthReport() {
    this.resetHealthReportForm();     
    this.dialog_report = true;
  },
      editItemReport(item) {
        this.editedIndex_health = this.health_cards.indexOf(item);
        const cloned = {
          id: item.id,
          attachment: null,
          'Under_medical_tab__health_reports_upload_date': "",
          'Under_medical_tab__health_reports_documentname': "",
          'Under_medical_tab__health_reports_description': "",
          'Under_medical_tab__health_reports_files': null,
        };
        item.fields.forEach(field => {
          if (field.field_name === 'Under_medical_tab__health_reports_upload_date' && field.value) {
            const months = {
              Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
              Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
            };
            const parts = field.value.split("/");
            if (parts.length === 3) {
              const day = parts[0].padStart(2, "0");
              const month = months[parts[1].substring(0, 3)];
              const year = parts[2];
              if (month) {
                cloned[field.field_name] = `${year}-${month}-${day}`;
              }
            }
          } else if (field.field_name === 'Under_medical_tab__health_reports_files') {
            cloned.attachment = field.value !== 'NA' ? field.value : null;
          } else {
            cloned[field.field_name] = field.value;
          }
        });
        this.editedItem_health = cloned;
        this.dialog_report = true;
      },
      close() {
        this.dialog = false;
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem);
          this.editedIndex = -1;
        }, 300);
      },
      closehealthreport() {
        this.resetHealthReportForm();
        this.dialog_report = false;
        setTimeout(() => {
          this.editedItem_health = Object.assign({}, this.defaultItem);
          this.editedIndex_health = -1;
        }, 300);
      },
      dialog_vaccin_close() {
        this.dialog_vaccin = false;
      },
      isValidated() {
        if (this.editedItem.nature_hprob) {
          return true;
        } else {
          if (!this.editedItem.nature_hprob) {
            this.errorMsg.name = true;
          }
          return false;
        }
      },
      resetHealthReportForm() {
        this.editedItem_health = {
          id: "",
          'Under_medical_tab__health_reports_upload_date': "",
          'Under_medical_tab__health_reports_documentname': "",
          'Under_medical_tab__health_reports_description': "",
          'Under_medical_tab__health_reports_files': null,
          attachment: null,
        };
        this.editedIndex_health = -1;
      },
      saveHealthReport() {
        for (let field of this.fieldConfigs) {
          if (field.required && field.field_name !== 'Under_medical_tab__health_reports_files') {
            if (!this.editedItem_health[field.field_name]) {
              this.showSnackbar("error", `Please enter ${field.label}`);
              return;
            }
          } else if (field.required && field.field_name === 'Under_medical_tab__health_reports_files') {
            if (!this.editedItem_health[field.field_name] && !this.editedItem_health.attachment) {
              this.showSnackbar("error", `Please upload ${field.label}`);
              return;
            }
          }
        }
        this.save_loader_report = true;
        let formData = new FormData();
        formData.append("ldid", this.child_div_id);
        if (this.editedIndex_health > -1) {
          formData.append("id", this.editedItem_health.id);
          this.fieldConfigs.forEach(field => {
            const shortName = field.field_name.replace("Under_medical_tab__health_reports_", "");
            if (field.field_name === 'Under_medical_tab__health_reports_files') {
              if (this.editedItem_health[field.field_name]) {
                formData.append("files", this.editedItem_health[field.field_name]);
              }
            } else {
              formData.append(shortName, this.editedItem_health[field.field_name] || "");
            }
          });
          axios
            .post("/parent/ediChildHealthDetails", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then((res) => {
              if (res.data.flag === "YES") {
                this.showSnackbar("green", res.data.snackbar || "Health report updated successfully.");
                this.health_cards = res.data.health_cards;
                this.resetHealthReportForm();
                this.dialog_report = false;
              } else {
                this.showSnackbar("error", res.data.snackbar || "Update failed");
              }
            })
            .catch((error) => {
              console.error(error);
              this.showSnackbar("error", "An error occurred during update.");
            })
            .finally(() => {
              this.save_loader_report = false;
            });
        } else {
          this.fieldConfigs.forEach(field => {
            const shortName = field.field_name.replace("Under_medical_tab__health_reports_", "");
            if (field.field_name === 'Under_medical_tab__health_reports_files') {
              if (this.editedItem_health[field.field_name]) {
                formData.append("files", this.editedItem_health[field.field_name]);
              }
            } else {
              formData.append(shortName, this.editedItem_health[field.field_name] || "");
            }
          });
          axios
            .post("/parent/saveChildHealthDetails", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then((res) => {
              if (res.data.flag === "YES") {
                this.showSnackbar("green", res.data.snackbar || "Health report added successfully.");
                this.health_cards = res.data.health_cards;
                this.dialog_report = false;
              } else {
                this.showSnackbar("error", res.data.snackbar || "Upload failed");
              }
            })
            .catch((error) => {
              console.error(error);
              this.showSnackbar("error", "An error occurred during upload.");
            })
            .finally(() => {
              this.save_loader_report = false;
            });
        }
      },
    },
    mounted() {
      this.learner = localStorage.getItem("learner_for_profile");
      if (this.learner) {
        this.getMedicalDetails();
      }
      this.awspath = localStorage.getItem("awspath");
    },
  };
</script>

<style scoped>
  .custom-toolbar {
    background: linear-gradient(to right, #a8edea, #e4d0ff);
  }

  .sibling-table td,
  tr,
  th {
    border-collapse: collapse;
  }

  .button-77 {
    color: #fff;
    cursor: pointer;
    font-weight: 800;
    vertical-align: middle;
    z-index: 0;
  }

  .button-77:before,
  .button-77:after {
    border-radius: 80px;
  }

  .button-77:before {
    background-color: rgba(249, 58, 19, 0.32);
    content: "";
  }

  .button-77:after {
    background-image: linear-gradient(92.83deg, #ff7426 0, #f93a13 100%);
    bottom: 4px;
    content: "";
    display: block;
    left: 4px;
    overflow: hidden;
    position: absolute;
    right: 4px;
    top: 4px;
    transition: all 100ms ease-out;
    z-index: -1;
  }

  .button-77:hover:not(:disabled):after {
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    transition-timing-function: ease-in;
  }

  .button-77:active:not(:disabled) {
    color: #ccc;
  }

  .button-77:active:not(:disabled):after {
    background-image: linear-gradient(0deg,
        rgba(0, 0, 0, 0.2),
        rgba(0, 0, 0, 0.2)),
      linear-gradient(92.83deg, #ff7426 0, #f93a13 100%);
    bottom: 4px;
    left: 4px;
    right: 4px;
    top: 4px;
  }

  .button-77:disabled {
    cursor: default;
    opacity: 0.24;
  }

  .main-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  .custom-tabs {
    background: linear-gradient(to right, #b2d8f7, #f4d1f4);
    border-radius: 30px;
    padding: 2px;
    min-height: 50px;
  }

  .custom-tab {
    font-weight: normal;
    color: black;
    text-transform: uppercase;
    font-size: 14px;
    min-height: 40px;
    border-radius: 0;
  }

  .first-tab {
    border-top-left-radius: 30px;
    border-bottom-left-radius: 30px;
  }

  .last-tab {
    border-top-right-radius: 30px;
    border-bottom-right-radius: 30px;
  }

  .active-tab {
    background: white !important;
    color: black !important;
    font-weight: bold !important;
  }

  .under-development {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background-color: #f5f6fa;
    text-align: center;
  }

  .content {
    padding: 20px;
    border: 2px dashed #ccc;
    border-radius: 10px;
    background-color: #fff;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  }

  h1 {
    color: #333;
    margin-top: 20px;
    font-size: 24px;
  }

  p {
    color: #666;
    font-size: 16px;
  }

  .custom-header {
    background: linear-gradient(to right, #ffe0b2, #ffccbc);
    color: black !important;
    font-weight: bold;
  }
</style>